import { HighlightCreationContext } from "context/HighlightCreation/context";
import useCallbackOnClipUpdate from "hooks/websocket/useClipUpdate";
import { useCallback, useContext, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { generateHighlightPreview, getClipListDetails } from "store/clipList/actions";
import {
  selectClipListDetailsById,
  selectIsAllClipsOfClipListSyncedWithFoldersAspectRatio,
} from "store/clipList/selectors";
import { updateClipListValues } from "store/clipList/slice";
import { selectAppAdditionalPermissionsZeepublishingBySelectedUserId } from "store/entityConfig/selectors";
import { selectSelectedUserId } from "store/user/selectors";

import { convertClipsToVideosData } from "../Videos/EditingVideoPlayer/utils";
import WebSocketGenerateHighlight from "../WebSocket/WebSocketGenerateHighlight";

const HighlightGenerationListener = ({
  userId,
  streamId,
  clipListId,
  onClipUpdate,
  aspectRatio,
  clipListDetails,
  isAllClipsSynced,
  clipListClips,
  zeePublishing,
  generateHighlightPreview,
  updateClipListValues,
  getClipListDetails,
}) => {
  const { highlights, setHighlights } = useContext(HighlightCreationContext);
  const hasInitiatedPreview = useRef(false);

  const createPreview = useCallback(async () => {
    try {
      const isProcessingHighlight =
        highlights?.length &&
        highlights?.find((highlight) => highlight?.clipListId === clipListId)?.status === "PROCESSING";

      if (isProcessingHighlight) {
        const videos = convertClipsToVideosData(clipListClips, aspectRatio);

        const filteredVideos = videos.filter(({ type }) => type !== "transition");
        const clipUrls = filteredVideos.map(({ url }) => url);
        const clipIds = filteredVideos
          .filter(({ type }) => type === "clip")
          .flatMap(({ clipData }) => (clipData ? [clipData.id] : []));
        const totalDuration = filteredVideos.reduce((acc, { duration }) => acc + duration, 0);

        const highlightApiPayload = {
          folderId: clipListId,
          clips: clipUrls,
          clipIdArr: clipIds,
          isHighlightVideo: true,
          isImage: false,
          isPreSlate: false,
          isPostSlate: false,
          isAudio: false,
          image: {},
          audio: "",
          preSlate: [],
          postSlate: [],
          isTransition: null,
          transitionName: "",
          selected_transiton: "",
          audio_intensity_array: [1, 0],
          slate_index: [],
          type: "highlight",
          overlayLogo: "",
          clipsinfo: videos,
          isForZeeClient: zeePublishing,
          skip_trans: [],
          userId,
          totalDuration,
          totalDurationWithoutBumper: totalDuration,
        };

        const response = await generateHighlightPreview({ payload: highlightApiPayload });

        if (response.payload.status) {
          setHighlights((prev) =>
            prev?.map((highlight) =>
              highlight.clipListId === clipListId ? { ...highlight, status: "GENERATING" } : highlight,
            ),
          );
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [
    aspectRatio,
    clipListClips,
    clipListId,
    generateHighlightPreview,
    highlights,
    setHighlights,
    userId,
    zeePublishing,
  ]);

  const updateClipsListDetails = useCallback(async () => {
    try {
      const currentHighlight = highlights?.find((highlight) => highlight?.clipListId === clipListId);
      if (currentHighlight?.status === "PROCESSING" && !hasInitiatedPreview.current) {
        hasInitiatedPreview.current = true;
        createPreview();
        updateClipListValues({ isPreview: true, progressPercent: 0, previewUrl: "", clipListId });
      }
    } catch (error) {
      console.error(error);
    }
  }, [clipListId, createPreview, highlights, updateClipListValues]);

  const refreshOnClick = useCallback(
    async (isSuccess) => {
      try {
        setHighlights((prev) =>
          prev?.map((highlight) =>
            highlight.clipListId === clipListId
              ? { ...highlight, status: isSuccess ? "SUCCESS" : "FAILED", isHidden: false }
              : highlight,
          ),
        );
        await getClipListDetails({ clipListId, isDragDropHLGeneration: true });
      } catch (error) {
        console.error(error);
      }
    },
    [clipListId, getClipListDetails, setHighlights],
  );

  useCallbackOnClipUpdate({
    streamId,
    onClipUpdate: () => onClipUpdate(clipListId),
  });

  useEffect(() => {
    const currentHighlight = highlights?.find((highlight) => highlight?.clipListId === clipListId);

    if (currentHighlight?.status !== "PROCESSING") {
      hasInitiatedPreview.current = false;
    }
    if (
      isAllClipsSynced &&
      clipListClips?.length &&
      clipListDetails?._id &&
      currentHighlight?.status === "PROCESSING"
    ) {
      updateClipsListDetails();
    }
  }, [clipListClips, clipListDetails?._id, isAllClipsSynced, updateClipsListDetails, clipListId, highlights]);

  return <WebSocketGenerateHighlight clipListId={clipListId} reloadFunc={refreshOnClick} isDragDropHL={true} />;
};

const mapStateToProps = createStructuredSelector({
  userId: selectSelectedUserId,
  clipListDetails: selectClipListDetailsById,
  isAllClipsSynced: selectIsAllClipsOfClipListSyncedWithFoldersAspectRatio,
  zeePublishing: selectAppAdditionalPermissionsZeepublishingBySelectedUserId,
});

const mapDispatchToProps = {
  generateHighlightPreview,
  updateClipListValues,
  getClipListDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(HighlightGenerationListener);
